// Task 35/40 (client decision) — Edit Quote: one EXISTING middle stop's editable fields (address,
// Pickup/Delivery services, Limited Access Delivery, and activity hint — arrival date/window and
// add/remove-stop remain locked post-creation). Adapted from AddQuote/Steps/StopDetails/index.tsx
// (the exact same pattern already built for the create flow), trimmed of date/window, which
// doesn't apply here. Unlike StopDetails (local useState, wizard-owned), this is a fully
// CONTROLLED component — QuoteDetailModalNew.tsx lifts the array of middle-stop edits into its
// own state (see IEditStopFormData in multiStop.helpers.ts) so it can compute save-button
// dirtiness and build the update payload; this component never holds its own copy of the data.
//
// Task 40 (revised) — the Pickup/Deliver/Both hint control is back (same ACTIVITY_HINT_OPTIONS
// pattern as StopDetails.tsx), and this hint IS now persisted:
// quote_stops.activityHint is written on save and seeded back on reopen (multiStop.helpers.ts's
// toEditStopFormData/buildEditStopPayload). It also still drives THIS editing session's
// service-section visibility, the address-list search direction, and (via DimensionsTab.tsx) Load
// Details' per-item routing dropdown hard-filtering — identical to how the hint behaves in Add
// Quote. locationId (revised, user decision) IS now persisted — quote_stops.locationId — mirroring
// activityHint's own round-trip; picking an existing saved record, creating a new one, or clearing
// back to a fresh typed address all update stop.locationId via updateField, not just the scoped
// RHF form ShipperConsigneeAutocomplete needs for its controlled input.
import { Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import React, { FC, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ShipperConsigneeForm from 'src/app/MyQuote/CreateQuote/Steps/ShipperConsigneeDetails/ShipperConsigneeForm';
import CheckBox from 'src/components/CheckBox';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { isShipperConsigneeAddressValid } from 'src/utils/CommonFunctions';

import { ACTIVITY_HINT, ActivityHintType, IEditStopFormData } from '../AddQuote/multiStop.helpers';

import AdditionalServiceModal from './AdditionalServiceModal';
import LimitedAccessDeliveryModal from './LimitedAccessDeliveryModal';
import { ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm } from './quoteDetailModal.interface';

interface IProps {
    stop: IEditStopFormData;
    onChange: (next: IEditStopFormData) => void;
    showAddressError?: boolean;
    // Bug fix (user-reported) — the heading below must show the same relative middle-stop number
    // ("Stop 1", "Stop 2", ...) as the sidebar tab that opens it, not the raw absolute route
    // position (stop.position). Falls back to stop.position if omitted.
    displayPosition?: number;
}

// Mirrors StopDetails.tsx's EDITABLE_SERVICES_SLUGS — gates the pencil-icon-once-checked pattern
// only, never filters the rendered list.
const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];
const NON_STOP_SUPPORTED_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods'];

// Mirrors StopDetails.tsx's ACTIVITY_HINT_OPTIONS/getActivityHintLabel exactly.
const ACTIVITY_HINT_OPTIONS: ActivityHintType[] = [ACTIVITY_HINT.PICKUP, ACTIVITY_HINT.DELIVERY, ACTIVITY_HINT.BOTH];

const getActivityHintLabel = (t: any, value: ActivityHintType): string => {
    if (value === ACTIVITY_HINT.PICKUP) return t('MyQuote.Activity_Pickup');
    if (value === ACTIVITY_HINT.DELIVERY) return t('MyQuote.Activity_Delivery');

    return t('MyQuote.Activity_Both');
};

const textReplacements = (t: any) => [
    { original: 'After Hours / Weekend / Holiday Pickup', replacement: t('QuotingHub.Common.After_Hours_Pickup') },
    { original: 'Pickup Appointment Required', replacement: t('QuotingHub.Common.Pickup_Appointment') },
    { original: 'Delivery Appointment Required', replacement: t('QuotingHub.Common.Delivery_Appointment') },
];

const EditStopDetails: FC<IProps> = ({ stop, onChange, showAddressError = false, displayPosition }) => {
    const { t } = useTranslation();
    const { serviceTypes, shipperOptions, consigneeOptions, shippers, consignees, handleSearchShipper, handleSearchConsignee } = useAddressDetails() as any;
    const serviceRefName = useRef<HTMLParagraphElement>(null);

    const [serviceInfoModal, setServiceInfoModal] = useState<'pickup' | 'delivery' | null>(null);
    const [showLimitedAccessModal, setShowLimitedAccessModal] = useState(false);
    const [isEditId, setIsEditId] = useState(false);

    const activityHint: ActivityHintType = stop.activityHint ?? ACTIVITY_HINT.BOTH;
    const activityHintOptions = ACTIVITY_HINT_OPTIONS.map((hintOption) => ({ label: getActivityHintLabel(t, hintOption), value: hintOption }));

    // Mirrors StopDetails.tsx's `isShipper` exactly (minus the locationType freeze, which needs a
    // persisted id/type this feature doesn't have — see file header): Deliver hint alone routes
    // to Consignees; Pick up or Both route to Shippers.
    const isShipper = activityHint !== ACTIVITY_HINT.DELIVERY;

    const { control: locationControl, setValue: setLocationValue } = useForm<{ locationId: number | null }>({
        defaultValues: { locationId: null },
    });

    const pickupServices = (serviceTypes?.PICKUP || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));
    const deliveryServices = (serviceTypes?.DELIVERY || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));

    const updateField = (patch: Partial<IEditStopFormData>) => onChange({ ...stop, ...patch });

    const resetLocationForm = () => {
        setLocationValue('locationId', null);
        updateField({ companyName: '', stopAddress: null, locationId: null });
    };

    const handleLocationChange = (onChangeRHF: any) => (value: any) => {
        if (value) {
            onChangeRHF(value.value);
            const source = isShipper ? shippers : consignees;
            const data = (source || []).find((s: any) => s.id === value.value);

            if (data) {
                updateField({ companyName: data.companyName, stopAddress: data, locationId: data.id });

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditId(true);
                }
            }
        } else {
            onChangeRHF(null);
            resetLocationForm();
        }
    };

    const handleGoogleAddressSelect = (addressData: any) => {
        setLocationValue('locationId', null);
        updateField({
            companyName: '',
            locationId: null,
            stopAddress: {
                fullAddress: addressData?.fullAddress ?? '',
                address1: addressData?.address1 ?? '',
                city: addressData?.city ?? '',
                country: addressData?.country ?? '',
                countryCode: addressData?.countryCode ?? '',
                latitude: addressData?.latitude ?? '',
                longitude: addressData?.longitude ?? '',
                postal: addressData?.postal ?? '',
                state: addressData?.state ?? '',
                stateCode: addressData?.stateCode ?? '',
                streetNumber: addressData?.streetNumber ?? '',
                timezone: addressData?.timezone ?? '',
                additionalServiceIds: addressData?.additionalServiceIds ?? [],
            },
        });
        setIsEditId(true);
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            setIsEditId(true);
        } else if (value === 'switch_id') {
            resetLocationForm();
        }
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        resetLocationForm();
    };

    const handleEditLocation = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);

        if (!isEdit) {
            resetLocationForm();
        }
    };

    const handleSaveLocation = (data: any) => {
        setIsEditId(false);
        setLocationValue('locationId', data.id);
        updateField({ companyName: data.companyName, stopAddress: data, locationId: data.id });

        const savedServiceIds: number[] = (data.pickupAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
        const allowedIds = new Set([...pickupServices, ...deliveryServices].map((s: any) => s.id));
        const filteredIds = savedServiceIds.filter((id: number) => allowedIds.has(id));

        if (filteredIds.length) {
            updateField({ serviceId: Array.from(new Set([...stop.serviceId, ...filteredIds])) });
        }
    };

    const handleLocationInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            if (isShipper) handleSearchShipper(e);
            else handleSearchConsignee(e);
        }
    };

    // Bug fix (user-reported, revised) — changing the Stop Activity value just previews a different
    // hint; it must NOT mutate serviceId. Doing so here immediately discarded whatever was already
    // saved in the DB (or entered this session) the moment the user merely selected a different
    // hint to look around, before ever saving. The actual prune-stale-services-for-the-current-hint
    // logic now runs once, at save time, in QuoteDetailModalNew.tsx's buildEditStopPayload call —
    // so browsing hints is non-destructive, and only a real Save commits the cleanup.
    const handleHintChange = (hint: ActivityHintType) => {
        updateField({ activityHint: hint });
    };

    const handleServiceChange = (e: any) => {
        const { checked, name } = e.target;
        const value = parseInt(e.target.value, 10);

        if (checked && name === 'limited_access_delivery') {
            setShowLimitedAccessModal(true);

            return;
        }

        updateField({
            serviceId: checked ? [...stop.serviceId, value] : stop.serviceId.filter((id) => id !== value),
            ...(name === 'limited_access_delivery' && !checked ? { limitedAccessDelivery: false, limitedAccessDeliveryType: '', limitedAccessDeliveryOther: '' } : {}),
        });
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'limited_access_delivery') {
            setShowLimitedAccessModal(true);
        }
    };

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        const limitedAccessId = deliveryServices.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        updateField({
            limitedAccessDelivery: true,
            limitedAccessDeliveryType: data.selectedLimitedAccessType,
            limitedAccessDeliveryOther: data.limitedAccessOther,
            serviceId: limitedAccessId && !stop.serviceId.includes(Number(limitedAccessId)) ? [...stop.serviceId, Number(limitedAccessId)] : stop.serviceId,
        });
        setShowLimitedAccessModal(false);
    };

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements(t).find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const renderServiceCheckboxes = (list: any[]) => (
        <div className="rounded-lg border border-utilityGray200 bg-white shadow-sm flex flex-wrap p-3 gap-3">
            {list.map((val: any) =>
                val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null ? (
                    <div className="flex w-[calc(33.33%_-_8px)]" key={`edit_stop_service_${val.id}`}>
                        <CheckBox
                            id={`edit_stop_service_${stop.id}_${val.id}`}
                            onChangeFunc={handleServiceChange}
                            name={val.slug}
                            value={val.id}
                            checked={stop.serviceId.includes(val.id)}
                            parentClassName="mt-px"
                        />
                        <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                            {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                <TooltipCmp message={val.name} childrenClassName="!flex">
                                    <label htmlFor={`edit_stop_service_${stop.id}_${val.id}`} className="cursor-pointer truncate">
                                        {getReplacedText(val.name)}
                                    </label>
                                </TooltipCmp>
                            ) : (
                                <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                    <p className="truncate" ref={serviceRefName}>
                                        <label htmlFor={`edit_stop_service_${stop.id}_${val.id}`} className="cursor-pointer">
                                            {getReplacedText(val.name)}
                                        </label>
                                    </p>
                                </TruncateTextCmp>
                            )}
                        </div>
                        {stop.serviceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                            <TooltipCmp message={t('Common.Edit')}>
                                <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                            </TooltipCmp>
                        ) : null}
                    </div>
                ) : null
            )}
        </div>
    );

    // Task 40 — mirrors StopDetails.tsx's showPickupServices/showDeliveryServices exactly, now
    // driven by the real (session-local) hint again instead of the derived unitsOn/unitsOff proxy.
    const showPickupServices = activityHint === ACTIVITY_HINT.PICKUP || activityHint === ACTIVITY_HINT.BOTH;
    const showDeliveryServices = activityHint === ACTIVITY_HINT.DELIVERY || activityHint === ACTIVITY_HINT.BOTH;

    return (
        <>
            <div className="space-y-1.5 flex flex-col">
                <h6 className="text-grayLight900 text-xs font-medium">
                    {t('MyQuote.Stop_N_Details', { n: displayPosition ?? stop.position })}
                    <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                </h6>
                <div className="p-3 flex-col rounded-lg border border-utilityGray200 bg-gray25 flex flex-wrap gap-x-2 space-y-3">
                    {/* Task 38 (user-reported) — this modal's tab content stacks sections with the compact
                "small h6 heading + bordered card" convention ShipperDetails.tsx/ConsigneeDetails.tsx
                use (spacing between sections comes from QuoteDetailModalNew.tsx's own wrapping
                `space-y-3` container), NOT the wide label-beside-content `stepper-content-container`
                layout — that's the Add Quote WIZARD page's own convention, meant for a full page,
                not this narrow modal. Also drops the map (ShipperDetails/ConsigneeDetails don't
                show one either — full address is already known from the picked/typed location). */}
                    <div className="space-y-1.5">
                        <h6 className="text-grayLight900 text-xs font-medium">{t('MyQuote.Stop_Activity')}</h6>
                        <SelectBox
                            parentClassName="w-full"
                            options={activityHintOptions}
                            value={activityHintOptions.find((opt) => opt.value === activityHint)}
                            onChangeFunc={(selectedOption: any) => handleHintChange(selectedOption.value)}
                            isSearchable={false}
                            isClearable={false}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <h6 className="text-grayLight900 text-xs font-medium">
                            {t('MyQuote.Stop_Address')}
                            <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                        </h6>
                        <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 flex flex-wrap gap-x-2 space-y-3">
                            {!isEditId && (
                                <ShipperConsigneeAutocomplete
                                    control={locationControl}
                                    isShipper={isShipper}
                                    isEditId={isEditId}
                                    watchCompanyName={stop.companyName}
                                    watchShipperAddress={stop.stopAddress}
                                    watchShipperFullAddress={stop.stopAddress?.fullAddress}
                                    shipperOptions={shipperOptions}
                                    consigneeOptions={consigneeOptions}
                                    errors={
                                        {
                                            shipperAddress: showAddressError ? { message: t('MyQuote.Stop_Address') } : undefined,
                                            consigneeAddress: showAddressError ? { message: t('MyQuote.Stop_Address') } : undefined,
                                        } as any
                                    }
                                    handleShipperChange={handleLocationChange}
                                    handleCreateNew={handleCreateNew}
                                    handleCardActionType={handleCardActionType}
                                    onInputChange={handleLocationInputChange}
                                    fieldName="locationId"
                                    isQuoteCreateForm={true}
                                    showDeleteOption={false}
                                    handleGoogleAddressSelect={handleGoogleAddressSelect}
                                    watchShipperConsigneeId={null}
                                    isOpenFromStop
                                />
                            )}
                            {isEditId && (
                                <ShipperConsigneeForm
                                    handleEditShipper={handleEditLocation}
                                    id={undefined}
                                    handleSaveShipper={handleSaveLocation}
                                    isShipper={isShipper}
                                    defaultCompanyName={stop.companyName || ''}
                                    defaultAddress={(stop.stopAddress as any) || null}
                                    showAddressIncompleteWarning
                                    isOpenFromStop
                                />
                            )}
                        </div>
                    </div>

                    {showPickupServices && (
                        <div className="space-y-1.5">
                            <div className="flex items-center mb-1.5">
                                <h6 className="form_label block mb-0">{t('QuotingHub.Common.Pickup_Additional_Services')}</h6>
                                <div className="cursor-pointer" onClick={() => setServiceInfoModal('pickup')}>
                                    <TooltipCmp message={t('QuotingHub.Common.Pickup_Additional_Services')}>
                                        <InfoCircle className="w-3.5 h-3.5 ml-1 text-primary cursor-pointer" />
                                    </TooltipCmp>
                                </div>
                            </div>
                            {renderServiceCheckboxes(pickupServices)}
                        </div>
                    )}

                    {showDeliveryServices && (
                        <div className="space-y-1.5">
                            <div className="flex items-center mb-1.5">
                                <h6 className="form_label block mb-0">{t('QuotingHub.Common.Delivery_Additional_Services')}</h6>
                                <div className="cursor-pointer" onClick={() => setServiceInfoModal('delivery')}>
                                    <TooltipCmp message={t('QuotingHub.Common.Delivery_Additional_Services')}>
                                        <InfoCircle className="w-3.5 h-3.5 ml-1 text-primary cursor-pointer" />
                                    </TooltipCmp>
                                </div>
                            </div>
                            {renderServiceCheckboxes(deliveryServices)}
                        </div>
                    )}
                </div>
            </div>

            {serviceInfoModal === 'pickup' && <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={pickupServices} title={`Pickup ${t('Common.Additional_Services')}`} />}
            {serviceInfoModal === 'delivery' && (
                <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={deliveryServices} title={`Delivery ${t('Common.Additional_Services')}`} />
            )}

            {showLimitedAccessModal && (
                <LimitedAccessDeliveryModal
                    handleClose={() => setShowLimitedAccessModal(false)}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={
                        {
                            limitedAccessType: stop.limitedAccessDeliveryType,
                            limitedAccessOther: stop.limitedAccessDeliveryOther,
                        } as ILimitedAccessDeliveryDataForm
                    }
                />
            )}
        </>
    );
};

export default EditStopDetails;
